import { DetailsPageTopControlsView } from "../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import Detail from "../common/panels/Detail/Detail";
import { Table } from "../common/panels/Detail/DetailTable";
import { NotAvailable, NotSet } from "../common/misc/UIconstants";
import { EntityDetailProps, EntityDetailTableProps } from "../common/entity/EntityInterfaces";
import { EntityDeleteButton } from "../Entities/GenericComponents/common/GenericEntityDetailPage";
import { Button } from "../common/buttons/Button/Button";
import { useCallback, useContext } from "react";
import { SessionContext } from "../common/contexts/SessionContext";
import { showtoast } from "../common/overlays/Toasts/showtoast";
import { CircularProgress } from "../common/loaders/CircularProgress/CircularProgress";
import { useQueryClient } from "@tanstack/react-query";
import { DateTimeRenderer } from "../common/datetime/DateTimeFormatter";
import { toUppercase } from "../common/helperfunctions/stringFunctions";
import { LucideIcon } from "../common/icon/LucideIcon";
import { jobStateToIcon, jobStateToLabel } from "../ViewerLayout/ViewerLayoutTypes";
import { FileExportJob, fileExportJobsConstants } from "../api/FileExportJobs";
import { useEntityDetail } from "../api/BaseEntityApi";
import { LiveConnectionCallbacks, useListen } from "../common/signalr/SignalRContext";

export const FileExportJobDetailTable = ({
  entity,
  entityConstants,
  fieldLabels,
  showLinks = true,
}: EntityDetailTableProps<"fileExportJobs">) => {
  return (
    <>
      <Table noPadding nonFoldable>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.name}
            content={
              <div className={"container_label"}>
                <div className={"container_label_name"} style={{ fontWeight: 600 }}>
                  {entity.name ?? NotSet}
                </div>
                <div className={"container_label_id"}>
                  <span style={{ whiteSpace: "nowrap" }}>
                    {toUppercase(entityConstants.entitySingular)}-ID: {entity.id}
                  </span>
                </div>
              </div>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.status}
            content={
              <div className="flex row-nowrap align-center gap-5">
                <LucideIcon name={jobStateToIcon(entity.status)} />
                {jobStateToLabel(entity.status)}
              </div>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.progress}
            content={
              <div className="flex row-nowrap align-center gap-5">
                <CircularProgress progress={entity.progress ?? 0} width={24} style={{ stroke: "var(--primary)" }} />
                <span style={{ color: "var(--gray-600)" }}>{entity.progress ?? 0}%</span>
              </div>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.numberOfItems}
            content={<span>{entity.numberOfItems ?? NotAvailable}</span>}
          />
          <Table.Body.RowContent
            title={fieldLabels.createdAt}
            content={
              !!entity.createdAt ? (
                <DateTimeRenderer date={entity.createdAt} includeElapsed={true} includeDate includeTime />
              ) : (
                NotSet
              )
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.expiresAt}
            content={
              !!entity.expiresAt ? (
                <DateTimeRenderer date={entity.expiresAt} includeElapsed={true} includeDate includeTime />
              ) : (
                NotSet
              )
            }
          />
          {!!entity.error && (
            <Table.Body.RowContent
              title={fieldLabels.error}
              content={<pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{entity.error}</pre>}
            />
          )}
        </Table.Body>
      </Table>
    </>
  );
};

export const FileExportJobDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"fileExportJobs">) => {
  const queryClient = useQueryClient();

  const FileExportJobCallback: LiveConnectionCallbacks["FileExportJobProgressChanged"] = useCallback(
    (jobId, progress) => {
      if (entity.id === jobId) {
        queryClient.invalidateQueries({ queryKey: [entityConstants.resource, "detail", entity.id] });
      }
    },
    [entity.id, entityConstants.resource, queryClient]
  );
  useListen("FileExportJobProgressChanged", FileExportJobCallback);

  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={entityConstants}
          hideHistoryModal
          controls={
            <div className="flex align-center gap-5">
              <FileExportDownloadButton id={entity.id} />
              <EntityDeleteButton
                entity={entity}
                entityTypeId={entityTypeId}
                alwaysDeletePermanently
                btnClassName={(entity) => "btn btn-danger"}
              />
            </div>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <div className="flex col-nowrap">
          <FileExportJobDetailTable
            entity={entity}
            entityConstants={entityConstants}
            fieldLabels={fieldLabels}
            entityTypeId={entityTypeId}
          />
        </div>
      }
      sidebarPosition="hidden"
    />
  );
};

export const FileExportDownloadButton = ({ id }: { id?: FileExportJob["id"] }) => {
  const { api } = useContext(SessionContext);
  const { data: job, isFetching } = useEntityDetail<FileExportJob>(
    fileExportJobsConstants.resource,
    id ?? 0,
    undefined,
    {
      enabled: !!id,
    }
  );
  return (
    <Button
      disabled={job?.status !== "finished"}
      loading={isFetching}
      className="btn btn-primary flex align-center gap-5"
      onClick={() => {
        const mediaType = !!job?.isSingleFile ? job.mediaType : "application/zip";
        api
          .post(`${fileExportJobsConstants.resource}/download/${id}`, {})
          .then((result) => {
            var blob = new Blob([result], {
              type: `${mediaType};charset=utf-8`,
            });
            var url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.target = "_blank";
            anchor.download = job?.name ?? `export_${id}`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
          })
          .catch((e) => {
            showtoast("error", "Download failed");
            console.error(e);
          });
      }}
    >
      <LucideIcon name="download" />
      Download
    </Button>
  );
};
